import React from 'react'
import { Box, Button } from '@mui/material'
import Card from '@mui/material/Card'
import { Typography } from '@mui/material'
import Translations from 'src/@core/layouts/components/Translations'
import { t } from 'i18next'
import { Doughnut } from 'react-chartjs-2'
import { DetailedLoad, LandState } from 'src/@types'
import { useRouter } from 'next/router'
import { numberFormatAsked } from 'src/@core/utils/number-format-asked'

function getGradient(chart: any) {
  const {
    ctx,
    chartArea: { left, right }
  } = chart
  const gradientSegment = ctx.createLinearGradient(left, 0, right, 0)
  gradientSegment.addColorStop(0, 'red')
  gradientSegment.addColorStop(0.5, 'yellow')
  gradientSegment.addColorStop(1, 'green')

  return gradientSegment
}

interface Props {
  detailedLoad: DetailedLoad
  landId: number
  status: LandState
}

const ProductionCard = ({ detailedLoad, landId, status }: Props) => {
  const router = useRouter()

  const data = {
    labels: [],
    datasets: [
      {
        labels: t('Average'),
        backgroundColor: (context: any) => {
          const chart = context.chart
          const { chartArea } = chart
          if (!chartArea) {
            return null
          }
          if (context.dataIndex === 0) {
            return getGradient(chart)
          } else {
            return 'black'
          }
        },
        borderColor: 'transparent',
        borderRadius: 20,
        data: [detailedLoad.grADPV],
        cutout: '85%',
        circumference: 180,
        rotation: 270,
        needleValue: detailedLoad.grADPV < 200 ? 1 : detailedLoad.grADPV > 500 ? 300 : detailedLoad.grADPV - 200
      }
    ]
  }

  const gaugeNeedle = {
    id: 'gaugeNeedle',
    afterDatasetDraw(chart: any) {
      const {
        ctx,
        data,
        chartArea: { width }
      } = chart

      ctx.save()
      const needleValue = data.datasets[0].needleValue
      const dataTotal = 500 - 200
      const angle = Math.PI + (1 / dataTotal) * needleValue * Math.PI
      const cx = width / 2
      const cy = chart._metasets[0].data[0].y

      ctx.lineWidth = 2
      ctx.translate(cx, cy)
      ctx.rotate(angle)
      ctx.beginPath()
      ctx.strokeStyle = 'black'
      ctx.moveTo(1, 1)
      ctx.lineTo(85, 0)
      ctx.stroke()

      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.strokeStyle = 'white'
      ctx.moveTo(1, 1)
      ctx.lineTo(60, 0)
      ctx.stroke()

      ctx.translate(-cx, -cy)
      ctx.beginPath()
      ctx.arc(cx, cy, 1, 0, 2)
      ctx.fillStyle = 'transparent'
      ctx.restore()

      ctx.font = '18px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseLine = 'bottom'
      ctx.fillText('Gr ADPV', cx, cy - 10)
    }
  }

  const config: any = {
    type: 'doughnut',
    data,
    options: {
      responsive: false,
      borderRadius: '20px',
      aspectRatio: 1.5,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: false
        }
      }
    }
  }

  return (
    <>
      <Card
        sx={{
          height: '495px',
          boxShadow: 'none',
          borderRadius: '20px',
          padding: '20px',
          position: 'relative',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start'
        }}
      >
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            paddingBottom: '10px'
          }}
          className='for-dark-bottom-border'
        >
          <Typography
            variant='h3'
            sx={{
              fontSize: '18px !important',
              fontWeight: 700
            }}
          >
            <Translations text={'Production'} />
          </Typography>
          <Typography
            sx={{
              color: '#222',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 400,
              letterSpacing: '-0.28px'
            }}
          >
            {detailedLoad.cycle.replace('_', '/')}
          </Typography>
        </Box>


        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', mb: 1 }}>
          <Box
            sx={{
              width: '90%',
              mt: '30%',
              pb: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute'
            }}
            className='for-dark-bottom-border'
          >
            <Typography
              variant='h3'
              sx={{
                fontSize: '24px !important',
                fontWeight: 700,
                color: 'black'
              }}
            >
              {numberFormatAsked(detailedLoad.grADPV)}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '12px !important',
              fontWeight: 600,
              mb: '10%'
            }}
          >
            <Translations text={'200'} />
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              minWidth: '165px',
              minHeight: '165px',
              maxWidth: '165px',
              maxHeight: '165px',
              ml: 5,
              mr: 5,
              mt: -5
            }}
          >
            <Doughnut data={data} options={config} height={82} plugins={[gaugeNeedle]} />

          </Box>
          <Typography
            sx={{
              fontSize: '12px !important',
              fontWeight: 600,
              mb: '10%'
            }}
          >
            <Translations text={'500'} />
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                width: '100%',
                backgroundColor: '#E8DAC1',
                borderRadius: '15px',
                padding: '8px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: '24px'
              }}
            >
              <Typography sx={{ fontWeight: 400, fontSize: '14px' }}>
                <Translations text={'Meat (Kg)'} />
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>
                {numberFormatAsked(detailedLoad.totalProduction)}
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                backgroundColor: '#E8DAC1',
                borderRadius: '15px',
                padding: '8px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: '24px'
              }}
            >
              <Typography sx={{ fontWeight: 400, fontSize: '14px' }}>
                <Translations text={'Meat per head (Kg)'} />
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>
                {numberFormatAsked(detailedLoad.productionPerHead)}
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                backgroundColor: '#E8DAC1',
                borderRadius: '15px',
                padding: '8px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography sx={{ fontWeight: 400, fontSize: '14px' }}>
                <Translations text={'Meat per livestock ha (Kg)'} />
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>
                {numberFormatAsked(detailedLoad.productionPerArea)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {status && (
          <Box sx={{ display: 'flex', justifyContent: 'center', w: '100%', alignItems: 'center', mt: 'auto' }}>
            <Button
              variant='outlined'
              sx={{
                color: '#213D36',
                width: '123px',
                padding: '8px, 12px, 8px, 12px',
                fontSize: '16px',
                fontWeight: 700,
                borderRadius: '30px',
                border: '3px solid #213D36'
              }}
              onClick={() => router.push(`/lands/details/rodeos/${landId}`)}
            >
              <Translations text={'See more'} />
            </Button>
          </Box>
        )}
      </Card>
    </>
  )
}

export default ProductionCard
